import { useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SupplierCategory, SupplierSummaryDto } from '@bhops/shared';
import { canEditMfg } from '@/permissions';
import { suppliersApi } from '@/api/suppliers';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { useDebounce } from '@/hooks/useDebounce';
import { SupplierCreateModal } from './SupplierCreateModal';

// docs/plan/v0.5.0-telemetry-supplier-inventory-predictive/plan.md §4.7 —
// Supplier admin page with KPI (defect rate / active parts ratio).

const CATEGORY_LABEL: Record<SupplierCategory, string> = {
  GRILLER_SPECIALIST: '그릴러 전문',
  LOADER_SPECIALIST: '로더 전문',
  SENSOR_SPECIALIST: '센서 전문',
  GENERAL: '일반',
  OVERSEAS: '해외',
};

const MIN_LOTS_FOR_RELIABLE_KPI = 5;

export function SuppliersPage() {
  const role = useAuthStore((s) => s.user?.role);
  // 계획서(2026-08-20) 요구1 ④ — 서버 suppliers.controller 의 등록·수정 @Roles 가
  // MFG_MANAGER 에 열렸다. canEditMfg 가 같은 집합의 화면 정본 — 안 맞추면
  // 제조팀은 라우트(결정 4)로 들어와도 버튼이 없는 반쪽 권한이 된다.
  const canCreate = role != null && canEditMfg(role);
  const [categoryFilter, setCategoryFilter] = useState<SupplierCategory | ''>('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);
  const [createOpen, setCreateOpen] = useState(false);

  const q = useQuery({
    queryKey: [
      'suppliers',
      {
        category: categoryFilter || undefined,
        active: activeOnly,
        search: debouncedSearch || undefined,
      },
    ],
    queryFn: () =>
      suppliersApi.list({
        category: categoryFilter || undefined,
        active: activeOnly,
        search: debouncedSearch || undefined,
      }),
    staleTime: 30_000,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header
        style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <div>
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            공급사
          </h1>
        </div>
        {canCreate && (
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="supplier-create-button"
          >
            + 공급사 등록
          </Button>
        )}
      </header>

      <SupplierCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />

      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          flexWrap: 'wrap',
          padding: '8px 12px',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
        }}
      >
        <input
          placeholder="코드나 이름으로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={input}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as SupplierCategory | '')}
          style={input}
        >
          <option value="">전체 분류</option>
          {(Object.keys(CATEGORY_LABEL) as SupplierCategory[]).map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <label
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 12,
            color: 'var(--fg-muted)',
          }}
        >
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />
          활성만
        </label>
        <span style={{ color: 'var(--fg-muted)', fontSize: 12, marginLeft: 'auto' }}>
          {q.isFetching ? '불러오는 중…' : `${q.data?.length ?? 0} 건`}
        </span>
      </div>

      <div
        style={{
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          background: 'var(--bg-surface)',
          overflow: 'auto',
        }}
      >
        {q.isLoading ? (
          <div style={emptyBlock}>불러오는 중…</div>
        ) : q.error ? (
          <div style={{ ...emptyBlock, color: 'var(--fg-danger)' }}>{extractApiError(q.error)}</div>
        ) : !q.data || q.data.length === 0 ? (
          <div style={emptyBlock}>
            {canCreate
              ? '등록된 공급사가 없습니다. 위쪽의 「+ 공급사 등록」을 눌러 추가해 주세요.'
              : '등록된 공급사가 없습니다.'}
          </div>
        ) : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>코드</th>
                <th style={th}>이름</th>
                <th style={th}>분류</th>
                <th style={th}>연락처</th>
                <th style={{ ...th, textAlign: 'right' }}>LOT</th>
                <th style={{ ...th, textAlign: 'right' }}>결함율</th>
                <th style={{ ...th, textAlign: 'right' }}>활성 부품</th>
                <th style={{ ...th, textAlign: 'right' }}>결함 부품 비율</th>
                <th style={{ ...th, textAlign: 'center' }}>활성</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((s) => (
                <SupplierRow key={s.id} supplier={s} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function SupplierRow({ supplier: s }: { supplier: SupplierSummaryDto }) {
  const reliable = s.kpi.totalLots >= MIN_LOTS_FOR_RELIABLE_KPI;
  const defectColor =
    s.kpi.defectRate >= 0.2
      ? 'var(--fg-danger)'
      : s.kpi.defectRate >= 0.05
        ? 'var(--severity-warning)'
        : 'var(--green-700)';

  return (
    <tr style={{ borderTop: '1px solid var(--border-subtle)', opacity: s.active ? 1 : 0.55 }}>
      <td style={{ ...tdMono, fontWeight: 600 }}>{s.code}</td>
      <td style={td}>{s.displayName}</td>
      <td style={td}>{s.category ? CATEGORY_LABEL[s.category] : '–'}</td>
      <td style={{ ...td, fontSize: 11, color: 'var(--fg-muted)' }}>
        {s.contactEmail ?? '–'}
        {s.contactPhone ? ` · ${s.contactPhone}` : ''}
      </td>
      <td style={{ ...tdMono, textAlign: 'right' }}>{s.kpi.totalLots}</td>
      <td
        style={{
          ...tdMono,
          textAlign: 'right',
          color: reliable ? defectColor : 'var(--fg-subtle)',
          fontStyle: reliable ? 'normal' : 'italic',
        }}
        title={
          reliable
            ? `의심 ${s.kpi.suspectedLots} · 확정 ${s.kpi.confirmedLots} / 전체 ${s.kpi.totalLots}`
            : `LOT가 ${s.kpi.totalLots}개뿐이라 아직 판단하기 이릅니다. ${MIN_LOTS_FOR_RELIABLE_KPI}개 이상이 좋습니다.`
        }
      >
        {reliable
          ? `${(s.kpi.defectRate * 100).toFixed(1)}%`
          : `(${(s.kpi.defectRate * 100).toFixed(1)}%)`}
      </td>
      <td style={{ ...tdMono, textAlign: 'right' }}>{s.kpi.activePartsTotal}</td>
      <td
        style={{
          ...tdMono,
          textAlign: 'right',
          color: s.kpi.defectiveActivePartsRatio > 0 ? 'var(--fg-danger)' : 'var(--fg-default)',
        }}
      >
        {(s.kpi.defectiveActivePartsRatio * 100).toFixed(1)}%
      </td>
      {/* 표의 예/아니오 통일 규약 — check = '예', em dash = '해당 없음'.
          docs/design/icon-vocabulary.md 참조. */}
      <td style={{ ...td, textAlign: 'center' }}>
        {s.active ? <Icon name="check" size={14} aria-hidden /> : '—'}
      </td>
    </tr>
  );
}

const input: CSSProperties = {
  height: 30,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
  color: 'var(--fg-default)',
  minWidth: 140,
};
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const tdMono: CSSProperties = { ...td, fontFamily: 'var(--font-mono)' };
const emptyBlock: CSSProperties = {
  padding: 40,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 13,
};
